// AICA BudgetBar Component
// Progress bar for billing budget visualization
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatDollars, DEFAULT_CURRENCY, type Currency } from '@/lib/currency';
import { AlertCircle } from 'lucide-react';

export interface BudgetBarProps {
  current: number;
  limit: number;
  currency?: Currency;
  showPercentage?: boolean;
  showWarning?: boolean;
  warningThreshold?: number; // Percentage (e.g., 80 for 80%)
  className?: string;
}

export const BudgetBar = memo(function BudgetBar({
  current,
  limit,
  currency = DEFAULT_CURRENCY,
  showPercentage = true,
  showWarning = true,
  warningThreshold = 80,
  className,
}: BudgetBarProps) {
  const { t } = useTranslation('common');
  const percentage = limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
  const isWarning = showWarning && percentage >= warningThreshold;
  const isDanger = showWarning && percentage >= 100;

  const formatMoney = (amount: number) => formatDollars(amount, currency);

  return (
    <div data-testid="budget-bar" className={cn('space-y-2', className)}>
      {/* Header: Current/Limit */}
      <div className="flex items-center justify-between text-sm">
        <div data-testid="budget-bar-amounts" className="flex items-center gap-2">
          <span
            data-testid="budget-bar-current"
            className="font-medium tabular-nums text-fg"
          >
            {formatMoney(current)}
          </span>
          <span className="text-fg-muted">{t('common.budgetBar.of', 'of')}</span>
          <span data-testid="budget-bar-limit" className="tabular-nums text-fg-muted">
            {formatMoney(limit)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {showPercentage && (
            <span
              data-testid="budget-bar-percentage"
              className={cn(
                'font-medium tabular-nums',
                isDanger ? 'text-danger' : isWarning ? 'text-warning' : 'text-fg-muted'
              )}
            >
              {percentage.toFixed(1)}%
            </span>
          )}
          {(isWarning || isDanger) && (
            <AlertCircle
              data-testid="budget-bar-warning-icon"
              className={cn('h-4 w-4', isDanger ? 'text-danger' : 'text-warning')}
            />
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div
        data-testid="budget-bar-progress"
        className="relative h-2 overflow-hidden rounded-full bg-bg-elevated"
      >
        <div
          className={cn(
            'h-full rounded-full',
            // Bloomberg-style: smooth linear progression
            'transition-all duration-150 ease-linear',
            'motion-reduce:transition-none',
            isDanger ? 'bg-danger' : isWarning ? 'bg-warning' : 'bg-primary'
          )}
          style={{ width: `${percentage}%` }}
        />

        {/* Shimmer effect when active */}
        {percentage > 0 && percentage < 100 && (
          <div
            className="absolute inset-0 animate-shimmer motion-reduce:animate-none"
            style={{
              background: `linear-gradient(
                90deg,
                transparent 0%,
                rgba(255, 255, 255, 0.1) 50%,
                transparent 100%
              )`,
              backgroundSize: '200% 100%',
            }}
          />
        )}
      </div>

      {/* Warning Message */}
      {isDanger && (
        <p data-testid="budget-bar-danger-message" className="text-xs text-danger">
          {t(
            'common.budgetBar.limitReached',
            "Budget limit reached. This is a spending alert: calls keep processing, but a bulk CRM import that doesn't fit the remaining budget is blocked until you raise the limit or the next month starts."
          )}
        </p>
      )}
      {isWarning && !isDanger && (
        <p data-testid="budget-bar-warning-message" className="text-xs text-warning">
          {t('common.budgetBar.usageWarning', {
            threshold: warningThreshold,
            defaultValue:
              'Budget usage is at {{threshold}}%. Consider increasing your limit.',
          })}
        </p>
      )}
    </div>
  );
});
